import React, { useEffect, useRef } from 'react';
import MessageBubble from './MessageBubble';
import WelcomeScreen from './chat/WelcomeScreen';
import ConfirmationDialog from './chat/ConfirmationDialog';
import StreamingIndicator from './chat/StreamingIndicator';
import { MessageListProps } from './types';

const MessageList: React.FC<MessageListProps> = ({
  messages,
  theme,
  welcomeMessage,
  pendingConfirmation,
  isLoading,
  isStreaming,
  statusMessage,
  onConfirmAction,
  onCancelConfirmation,
  onSuggestionClick,
  onDismissUpdate,
  onEditMessage,
  suggestions
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const hasInitialScrolled = useRef(false);

  useEffect(() => {
    if (!messagesEndRef.current) return;
    // First load: instant jump to bottom, no animation
    if (!hasInitialScrolled.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
      hasInitialScrolled.current = true;
      return;
    }
    // Subsequent updates (new message / streaming): smooth scroll
    if (hasInitialScrolled.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading, isStreaming]);

  return (
    <div
      ref={messagesContainerRef}
      className="flex-1 hover-scroll px-4 py-4 space-y-0.5"
    >
      {(isLoading || isStreaming) && (
        <div className="loading-bar mb-3 rounded-full" />
      )}

      <WelcomeScreen
        welcomeMessage={welcomeMessage || ''}
        suggestions={suggestions || []}
        onSuggestionClick={(s) => onSuggestionClick?.(s)}
      />

      {messages.map((message, index) => (
        <div key={message.id} className="relative group">
          <MessageBubble
            message={message}
            theme={theme}
            isUser={message.role === 'user'}
            onSuggestionClick={onSuggestionClick}
            {...(message.meta?.isSystemUpdate && onDismissUpdate ? { onDismiss: onDismissUpdate } : {})}
          />
          {message.role === 'user' && onEditMessage && (
            <div className="flex justify-end -mt-1 mb-1">
              <button
                onClick={() => onEditMessage(index)}
                className="opacity-0 group-hover:opacity-100 px-1.5 py-0.5 rounded text-slate-400 hover:text-blue-500 transition-all text-[9px] flex items-center gap-0.5"
                title="Edit"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
            </div>
          )}
        </div>
      ))}

      {pendingConfirmation && onConfirmAction && onCancelConfirmation && (
        <ConfirmationDialog
          pendingConfirmation={pendingConfirmation}
          isLoading={isLoading || false}
          onConfirm={onConfirmAction}
          onCancel={onCancelConfirmation}
        />
      )}

      {(isLoading || isStreaming) && (
        <StreamingIndicator
          statusMessage={statusMessage || ''}
          isStreaming={isStreaming || false}
        />
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
